// The "Client" field of the New Appointment dialog: an Autocomplete over the team's own
// clients, with free text still allowed for someone who has no patient record yet.
//
// WHY THIS REPLACED A TEXT FIELD CALLED "Client ID *"
// appointments.patient_id is NOT an id anybody can know or invent. It is the client's PHONE
// NUMBER (country code immediately followed by the number, e.g. 852 + 91234567), hashed on
// the way out by AppointmentData.saveAppointment (secureHash → p_patient_id) and stored
// encrypted. MindVault matches a booking to a client account with exactly that value:
// Tikcare-MindVault/src/pages/Therapist/hooks/useAppointments.ts decrypts patient_id,
// unhashes it and compares it to the signed-in client's `phone_code + phone_number`. A
// mistyped or invented "Client ID" therefore produces an appointment that simply never
// appears in the client's app — silently, with no error anywhere.
//
// So patient_id is DERIVED here and never typed: from the picked client's phone, or (free
// text path) from the phone code + phone number fields the therapist fills in below.
// The helpers are pure and exported so both the dialog and its tests use the same mapping.

import React, { useMemo } from 'react';
import { Autocomplete, TextField, Box, Typography, Alert } from '@mui/material';

// What a therapist needs to be told when there is no phone to derive patient_id from.
// The appointment still saves — it just cannot reach the client's app, and nothing else in
// the product says so.
export const NO_PHONE_WARNING =
  "No phone number is recorded for this client. Scheduling is still available; " +
  "MindVault visibility depends on a verified account link.";

const text = (value) => (value == null ? '' : String(value).trim());

// The stored phone code is a bare dialling code ('852'), and MindVault compares
// `phone_code + phone_number` character for character, so only strip what is decoration.
const normalizeCode = (code) => text(code).replace(/^\+/, '').replace(/\s/g, '');

// patient_id = country code + phone number. The single place this concatenation is spelled
// out; see the module comment for why it has to match MindVault's useAppointments.ts.
export function derivePatientId(phoneCode, phoneNumber) {
  const code = normalizeCode(phoneCode);
  const number = text(phoneNumber);
  if (!number) return '';
  return `${code}${number}`;
}

export function clientDisplayName(client = {}) {
  return (
    text(client.name) ||
    [text(client.firstName), text(client.lastName)].filter(Boolean).join(' ')
  );
}

export function clientPhoneNumber(client = {}) {
  return text(client.phoneNumber ?? client.phone_number);
}

export function clientHasPhone(client) {
  return Boolean(clientPhoneNumber(client));
}

// Email is the identity key everywhere else in the product (check_patient_email_unique,
// get_scribe_connection_statuses, purchase_connection_with_credits), so it is what
// disambiguates two clients with the same name.
export function clientOptionLabel(client = {}) {
  const name = clientDisplayName(client);
  const email = text(client.email);
  if (name && email) return `${name} (${email})`;
  return name || email || '';
}

export function toClientOption(client = {}) {
  const phoneCode = normalizeCode(client.phone_code);
  const phoneNumber = clientPhoneNumber(client);
  return {
    id: client.id,
    client,
    name: clientDisplayName(client),
    email: text(client.email),
    phone: phoneNumber ? `+${phoneCode || ''} ${phoneNumber}`.trim() : '',
    label: clientOptionLabel(client),
  };
}

// The six form fields one pick fills. patient_id is derived from the client's phone here
// and nowhere else.
export function appointmentFieldsForClient(client = {}) {
  const phoneCode = normalizeCode(client.phone_code);
  const phoneNumber = clientPhoneNumber(client);
  const gender = text(client.gender).toLowerCase();
  return {
    patient_name: clientDisplayName(client),
    // Empty when the client has no phone on file — NO_PHONE_WARNING explains what that costs.
    patient_id: derivePatientId(phoneCode, phoneNumber),
    phone_code: phoneCode || '852',
    phone_number: phoneNumber,
    // The dialog's Gender select only offers Male/Female; anything else (blank, unset)
    // falls back to the same default an empty form starts with rather than guessing.
    gender: gender === 'female' || gender === '女' ? 'Female' : 'Male',
    age: client.age == null ? '' : String(client.age),
  };
}

// The free-text path: the phone fields are the source, so every edit to one of them
// re-derives patient_id. Used by the dialog's handleFormChange.
export function appointmentFieldsForPhoneChange(form = {}, field, value) {
  const next = { ...form, [field]: value };
  return {
    [field]: value,
    patient_id: derivePatientId(next.phone_code, next.phone_number),
  };
}

export default function ClientPicker({
  clients = [],
  loading = false,
  form = {},
  disabled = false,
  error = false,
  helperText = '',
  onPickClient,
  onTypeName,
}) {
  const options = useMemo(
    () => clients.map(toClientOption).filter((option) => option.label),
    [clients],
  );

  const typedName = text(form.patient_name);
  const hasPhone = Boolean(text(form.phone_number));
  const showNoPhoneWarning = Boolean(typedName) && !hasPhone;

  return (
    <Box sx={{ width: '100%' }}>
      <Autocomplete
        freeSolo
        fullWidth
        size="small"
        disabled={disabled}
        loading={loading}
        options={options}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        inputValue={form.patient_name ?? ''}
        value={null}
        onInputChange={(event, newInputValue, reason) => {
          // 'reset' fires when an option is chosen; that name arrives through onPickClient
          // together with the other five fields, so it must not be handled twice.
          if (reason === 'input' || reason === 'clear') onTypeName?.(newInputValue);
        }}
        onChange={(event, option) => {
          if (!option) {
            onTypeName?.('');
            return;
          }
          if (typeof option === 'string') {
            // Free text: a client who is not a patient record yet. Only the name is known,
            // the phone fields below stay the source for patient_id.
            onTypeName?.(option);
            return;
          }
          onPickClient?.(appointmentFieldsForClient(option.client), option.client);
        }}
        renderOption={(props, option) => {
          const { key, ...liProps } = props;
          return (
            <Box component="li" key={option.id ?? option.label} {...liProps}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                  {option.name || option.email}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {[option.email, option.phone].filter(Boolean).join(' · ') || 'No email on file'}
                </Typography>
              </Box>
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Client"
            placeholder="Search your clients by name or email"
            error={error}
            helperText={helperText || 'Pick a client, or type a name to book someone new.'}
          />
        )}
      />
      {showNoPhoneWarning && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {NO_PHONE_WARNING}
        </Alert>
      )}
    </Box>
  );
}
